import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plus,
  ChevronDown,
  ChevronRight,
  Users,
  Trash2,
  LogOut,
  UserCog,
} from "lucide-react";
import useCalendar from "../../hooks/useCalendar";
import CalendarModal from "./CalendarModal";
import CalendarMembersModal from "./CalendarMembersModal";
import AlertModal from "./AlertModal";

/**
 * 사이드바 컴포넌트
 * @param {Array} selectedCalendars - 선택된 캘린더 ID 배열
 * @param {function} onCalendarSelect - 캘린더 선택 핸들러
 */
export default function Sidebar({ selectedCalendars = [], onCalendarSelect }) {
  const { fetchMyCalendars, createCalendar, deleteCalendar, leaveCalendar } =
    useCalendar();
  const [calendars, setCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedCalendarForMembers, setSelectedCalendarForMembers] =
    useState(null);
  const [expandedGroups, setExpandedGroups] = useState({
    personal: true,
    shared: true,
  });

  useEffect(() => {
    loadCalendars();
  }, []);

  const loadCalendars = async () => {
    setLoading(true);
    const result = await fetchMyCalendars();
    if (result.ok) {
      setCalendars(result.calendars);
      // 처음 로드 시 모든 캘린더 선택
      const allCalendarIds = result.calendars.map((cal) => cal.id);
      onCalendarSelect(allCalendarIds);
    }
    setLoading(false);
  };

  const handleCreateCalendar = async (calendarData) => {
    const result = await createCalendar(calendarData);
    if (result.ok) {
      setShowCalendarModal(false);
      loadCalendars();
      setAlertConfig({
        title: "성공",
        message: "캘린더가 생성되었습니다.",
        type: "success",
      });
      setShowAlert(true);
    } else {
      setAlertConfig({
        title: "오류",
        message: "캘린더 생성에 실패했습니다.",
        type: "error",
      });
      setShowAlert(true);
    }
  };

  const handleDeleteCalendar = async (calendar) => {
    setAlertConfig({
      title: "캘린더 삭제",
      message: `"${calendar.calendar_name}" 캘린더를 삭제하시겠습니까?`,
      type: "warning",
      onConfirm: async () => {
        const result = await deleteCalendar(calendar.id);
        if (result.ok) {
          loadCalendars();
          setAlertConfig({
            title: "성공",
            message: "캘린더가 삭제되었습니다.",
            type: "success",
          });
          setShowAlert(true);
        } else if (result.permissionDenied) {
          setAlertConfig({
            title: "권한 없음",
            message: "캘린더를 삭제할 권한이 없습니다.",
            type: "error",
          });
          setShowAlert(true);
        } else if (result.notFound) {
          setAlertConfig({
            title: "오류",
            message: "캘린더를 찾을 수 없습니다.",
            type: "error",
          });
          setShowAlert(true);
        }
      },
    });
    setShowAlert(true);
  };

  const handleLeaveCalendar = async (calendar) => {
    const result = await leaveCalendar(calendar.id);
    if (result.shouldDelete) {
      setAlertConfig({
        title: "캘린더 삭제 필요",
        message: "개인 캘린더는 떠날 수 없습니다. 삭제하시겠습니까?",
        type: "warning",
        onConfirm: () => handleDeleteCalendar(calendar),
      });
      setShowAlert(true);
    } else if (result.shouldMandate) {
      setAlertConfig({
        title: "권한 위임 필요",
        message: "방장은 다른 멤버에게 권한을 위임한 후 떠날 수 있습니다.",
        type: "info",
      });
      setShowAlert(true);
    } else if (result.ok) {
      loadCalendars();
      setAlertConfig({
        title: "성공",
        message: "캘린더에서 나갔습니다.",
        type: "success",
      });
      setShowAlert(true);
    }
  };

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleCalendarToggle = (calendarId) => {
    if (selectedCalendars.includes(calendarId)) {
      onCalendarSelect(selectedCalendars.filter((id) => id !== calendarId));
    } else {
      onCalendarSelect([...selectedCalendars, calendarId]);
    }
  };

  const personalCalendars = calendars.filter(
    (c) => c.calendar_type === "personal"
  );
  const sharedCalendars = calendars.filter((c) => c.calendar_type === "shared");

  return (
    <div className="w-64 h-full bg-white border-r flex flex-col">
      {/* 헤더 */}
      <div className="p-4 border-b">
        <button
          onClick={() => setShowCalendarModal(true)}
          className="w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />새 캘린더
        </button>
      </div>

      {/* 캘린더 목록 */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-500">로딩 중...</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* 개인 캘린더 */}
            <div>
              <button
                onClick={() => toggleGroup("personal")}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 mb-2"
              >
                <span>개인 캘린더</span>
                {expandedGroups.personal ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedGroups.personal && (
                <div className="space-y-1">
                  {personalCalendars.length === 0 ? (
                    <p className="text-sm text-gray-500 pl-4">
                      캘린더가 없습니다
                    </p>
                  ) : (
                    personalCalendars.map((calendar) => (
                      <div
                        key={calendar.id}
                        className="flex items-center justify-between group hover:bg-gray-50 rounded-lg p-2"
                      >
                        <label className="flex items-center flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedCalendars.includes(calendar.id)}
                            onChange={() => handleCalendarToggle(calendar.id)}
                            className="mr-3"
                          />
                          <Calendar className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-sm">
                            {calendar.calendar_name}
                          </span>
                        </label>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleDeleteCalendar(calendar)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <Trash2 className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* 공유 캘린더 */}
            <div>
              <button
                onClick={() => toggleGroup("shared")}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 mb-2"
              >
                <span>공유 캘린더</span>
                {expandedGroups.shared ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedGroups.shared && (
                <div className="space-y-1">
                  {sharedCalendars.length === 0 ? (
                    <p className="text-sm text-gray-500 pl-4">
                      캘린더가 없습니다
                    </p>
                  ) : (
                    sharedCalendars.map((calendar) => (
                      <div
                        key={calendar.id}
                        className="flex items-center justify-between group hover:bg-gray-50 rounded-lg p-2"
                      >
                        <label className="flex items-center flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedCalendars.includes(calendar.id)}
                            onChange={() => handleCalendarToggle(calendar.id)}
                            className="mr-3"
                          />
                          <Users className="w-4 h-4 text-gray-500 mr-2" />
                          <span className="text-sm">
                            {calendar.calendar_name}
                          </span>
                          {calendar.permission === "view" && (
                            <span className="ml-1 text-xs text-gray-400">
                              (보기)
                            </span>
                          )}
                        </label>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center">
                          <button
                            onClick={() => {
                              setSelectedCalendarForMembers(calendar);
                              setShowMembersModal(true);
                            }}
                            className="p-1 hover:bg-gray-200 rounded mr-1"
                            title="멤버 관리"
                          >
                            <UserCog className="w-4 h-4 text-gray-500" />
                          </button>
                          {calendar.owner_id === "current_user_id" ? ( // 실제 구현시 현재 유저 ID로 변경
                            <button
                              onClick={() => handleDeleteCalendar(calendar)}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <Trash2 className="w-4 h-4 text-gray-500" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleLeaveCalendar(calendar)}
                              className="p-1 hover:bg-gray-200 rounded"
                            >
                              <LogOut className="w-4 h-4 text-gray-500" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Calendar Modal */}
      <CalendarModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        onSave={handleCreateCalendar}
      />

      {/* Members Modal */}
      <CalendarMembersModal
        isOpen={showMembersModal}
        onClose={() => {
          setShowMembersModal(false);
          setSelectedCalendarForMembers(null);
        }}
        calendar={selectedCalendarForMembers}
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        {...alertConfig}
      />
    </div>
  );
}
